import { createTheme, Theme } from '@mui/material/styles';

// ─────────────────────────────────────────────────
// Brand palette — single place to swap company colors
// ─────────────────────────────────────────────────
export const brandPalette = {
  primary:        '#0d5c6b',
  primaryLight:   '#0d7a8f',
  primaryDark:    '#0a6a7d',
  secondary:      '#0097A7',   // legacy teal kept as secondary
  secondaryDark:  '#006166',

  heading:        '#1a2535',
  text:           '#2d3748',
  textSecondary:  '#5a6475',
  textMuted:      '#7a8799',
  muted:          '#8a96a6',

  bg:             '#f0f4f7',
  surface:        '#ffffff',
  surfaceAlt:     '#f5f8fb',   // table header bg, hover
  border:         '#dde5ed',
  borderLight:    '#edf1f5',

  success:        '#16a34a',
  error:          '#e85c4a',
  warning:        '#FEBD2B',
  warningLight:   '#FFD67B',

  // sidebar
  sidebarBg:      '#ffffff',
  sidebarActive:  '#e8f4f6',

  // badge - pickup
  badgePickupBg:      '#fdf3d0',
  badgePickupText:    '#a07c10',
  badgePickupBorder:  '#f0d980',
  // badge - land
  badgeLandBg:        '#fde0dc',
  badgeLandText:      '#b93a2e',
  badgeLandBorder:    '#f5b5af',
  // badge - business
  badgeBusinessBg:    '#e8edf2',
  badgeBusinessText:  '#5a6475',
  badgeBusinessBorder:'#c8d3dd',
  // badge - village
  badgeVillageBg:     '#dcf5e7',
  badgeVillageText:   '#1a7a3a',
  badgeVillageBorder: '#a6e3bc',

  // dark mode surfaces
  darkBg:         '#1a1f2e',
  darkSurface:    '#232835',
  darkSurfaceAlt: '#2a2f3e',
  darkBorder:     '#3a4050',
} as const;

// ─────────────────────────────────────────────────
// MUI theme builder
// ─────────────────────────────────────────────────
declare module '@mui/material/styles' {
  interface Theme {
    fontFamilyMono: string;
  }
  interface ThemeOptions {
    fontFamilyMono?: string;
  }
}

export function buildAppTheme(mode: 'light' | 'dark' = 'light'): Theme {
  const isLight = mode === 'light';

  return createTheme({
    fontFamilyMono: "'DM Mono', monospace",

    palette: {
      mode,
      primary: {
        main:  brandPalette.primary,
        light: brandPalette.primaryLight,
        dark:  brandPalette.primaryDark,
      },
      secondary: {
        main: brandPalette.secondary,
        dark: brandPalette.secondaryDark,
      },
      background: {
        default: isLight ? brandPalette.bg      : brandPalette.darkBg,
        paper:   isLight ? brandPalette.surface  : brandPalette.darkSurface,
      },
      text: {
        primary:   isLight ? brandPalette.text          : '#e0e0e0',
        secondary: isLight ? brandPalette.textSecondary : '#a0a8b4',
      },
      success: { main: brandPalette.success },
      error:   { main: brandPalette.error },
      warning: { main: brandPalette.warning },
      divider: isLight ? brandPalette.border : brandPalette.darkBorder,
    },

    typography: {
      fontFamily: "'DM Sans', sans-serif",
    },

    shape: {
      borderRadius: 8,
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isLight ? brandPalette.bg : brandPalette.darkBg,
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          underline: {
            '&:before':                              { borderBottomColor: brandPalette.primaryDark },
            '&:hover:not(.Mui-disabled):before':     { borderBottomColor: brandPalette.primary },
            '&:after':                               { borderBottomColor: brandPalette.primary },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: brandPalette.textSecondary,
            '&.Mui-focused': { color: brandPalette.primary },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: brandPalette.textSecondary,
            '&.Mui-focused': { color: brandPalette.primary },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            color: brandPalette.text,
            '&.Mui-focused': { color: brandPalette.primary },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            backgroundColor: brandPalette.primaryLight,
            '&:hover': { backgroundColor: brandPalette.primaryDark },
          },
        },
      },
    },
  });
}
